import React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from '../../Icons/Check';
import { ClickEvent } from '../../utils/tracking/types';
import { trackInteractionClick } from '../../utils/tracking/tracking';

enum ECheckboxSize {
	lg = 'w-4 h-4',
	md = 'w-4 h-4',
	sm = 'w-4 h-4',
	xs = 'w-3 h-3',
}

enum ECheckSize {
	lg = '20',
	md = '20',
	sm = '20',
	xs = '12',
}

enum ECheckboxLabelSize {
	lg = 'text-lg md:text-xl',
	md = 'text-lg',
	sm = 'text-base',
	xs = 'text-sm',
}

type CheckboxSize = keyof typeof ECheckboxSize;

interface ICheckboxProps {
	id?: string;
	size?: CheckboxSize;
	title: string;
	label: string | React.ReactNode;
	mandatory?: boolean;
	disabled?: boolean;
	defaultChecked?: boolean;
	checked?: boolean;
	hasError?: boolean;
	className?: string;
	labelClassName?: string;
	rootClassName?: string;
	setChecked?: (checked: boolean) => void;
	tracking?: ClickEvent;
	['data-cy']?: string;
}

const getBackgroundColor = (checked: boolean, disabled: boolean): string => {
	if (checked && !disabled) {
		return 'bg-gray-200';
	}
	if (checked && disabled) {
		return 'bg-gray-600';
	}
	return 'bg-white';
};

const getBorderColor = (checked: boolean, disabled: boolean, error: boolean): string => {
	if (error) {
		return 'border-error';
	}
	if (checked) {
		return 'border-none';
	}
	return disabled ? 'border-gray-600' : 'border-gray-350 hover:border-gray-200';
};

const getFillColor = (disabled: boolean) => {
	if (disabled) {
		return '#A3A3A3';
	}
	return 'white';
};

//Please use defaultChecked when you do not need to control its checked state
//(e.g.without checked / setChecked).
export const Checkbox = ({
	size = 'lg',
	title,
	label,
	id,
	mandatory = false,
	disabled = false,
	defaultChecked = false,
	checked = false,
	hasError = false,
	setChecked = () => checked,
	tracking,
	className,
	labelClassName,
	rootClassName,
	'data-cy': dataCy = undefined,
}: ICheckboxProps) => {
	const checkedState = defaultChecked || checked;
	const textColor = checked || defaultChecked ? 'text-gray-100' : 'text-gray-200';
	const currId = id || title;
	return (
		<div className={`cursor-pointer flex gap-2 ${className}`}>
			<CheckboxPrimitive.Root
				data-cy={dataCy ?? `checkbox-${label}`}
				checked={checkedState}
				tabIndex={disabled ? -1 : 0}
				disabled={disabled}
				title={title}
				aria-label={title}
				id={currId}
				className={`cursor-pointer border-solid inline-flex self-baseline flex-shrink-0 items-center justify-center border relative top-0.5 focus-visible:outline ${getBorderColor(
					checkedState,
					disabled,
					hasError,
				)} rounded-sm ${ECheckboxSize[size]} ${getBackgroundColor(checkedState, disabled)} ${rootClassName}`}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						setChecked(!checked);
						if (tracking) {
							trackInteractionClick(tracking);
						}
					}
				}}
				onClick={() => {
					setChecked(!checked);
					if (tracking) {
						trackInteractionClick(tracking);
					}
				}}>
				<CheckboxPrimitive.Indicator className="inline-flex">
					<Check size={ECheckSize[size]} fill={getFillColor(disabled)} />
				</CheckboxPrimitive.Indicator>
			</CheckboxPrimitive.Root>
			<label
				htmlFor={currId}
				className={`${mandatory && `before:content-['*_'] before:text-error`} cursor-pointer ${
					ECheckboxLabelSize[size]
				} ${disabled ? 'text-gray-400' : textColor} ${labelClassName}`}>
				{label}
			</label>
		</div>
	);
};
